import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/vue-query'
import {
  readNotificationDefaults,
  updateNotificationDefaults,
  type NotificationDefaults,
} from '@/api/notifications/defaults'
import {
  readNotificationPreferences,
  updateNotificationPreferences,
  type NotificationChoiceChange,
  type NotificationKind,
  type NotificationPreferences,
} from '@/api/notifications/preferences'
import {
  readWebsiteNotificationPreferences,
  updateWebsiteNotificationPreferences,
  type WebsiteNotificationPreferences,
} from '@/api/notifications/websitePreferences'
import {
  readWorkspaceNotificationPreferences,
  updateWorkspaceNotificationPreferences,
  type WorkspaceNotificationPreferences,
} from '@/api/notifications/workspacePreferences'
import {
  countNotifications,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/api/notifications/feed'
import { FEED_PAGE, UNREAD_POLL_MS } from './feed'
import { followChanges, remainingFollowChanges, type ScopedKindPreference } from './preferences'

// The notification reads, shared by the settings screens, the workspace screen, a channel's
// monetization settings and the bell. No organization in any key: the router clears the whole cache
// when the organization changes.

/**
 * The notification keys. The caller's organization-wide view prefixes every workspace and channel
 * view, because each answers from the other: a workspace's or a channel's value falls back to the
 * organization-wide one, and the organization-wide view lists the choices made for them. The
 * defaults reach the caller's own values too, so a defaults write refreshes both.
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  /** The caller's organization-wide view, and the prefix of every workspace and channel view. */
  preferences: ['notifications', 'preferences'] as const,
  /** Every workspace view, and nothing else. */
  workspaces: ['notifications', 'preferences', 'workspace'] as const,
  workspace: (workspaceId: string) => ['notifications', 'preferences', 'workspace', workspaceId] as const,
  /** Every channel view, and nothing else. */
  websites: ['notifications', 'preferences', 'website'] as const,
  website: (websiteId: string) => ['notifications', 'preferences', 'website', websiteId] as const,
  defaults: ['notifications', 'defaults'] as const,
  /**
   * The feed: its first page and its unread count. Every write that changes what the feed shows —
   * reading a line, and any change of a choice or a default, since a line is shown only while the
   * caller still wants its kind in the application — reads both again.
   */
  feed: ['notifications', 'feed'] as const,
  feedPage: ['notifications', 'feed', 'page'] as const,
  unreadCount: ['notifications', 'feed', 'unread-count'] as const,
}

type Enabled = { enabled?: MaybeRefOrGetter<boolean> }

/** The caller's notifications across the organization; any member may read them. */
export function useNotificationPreferences(options: Enabled = {}) {
  return useQuery({
    queryKey: notificationKeys.preferences,
    queryFn: ({ signal }) => readNotificationPreferences(signal),
    enabled: computed(() => toValue(options.enabled) !== false),
  })
}

/**
 * Set or clear the caller's organization-wide choices. The answer is the view as it now stands,
 * written into its key at once; every workspace and channel view falls back to it, so they are read
 * again.
 */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (changes: NotificationChoiceChange[]) => updateNotificationPreferences({ changes }),
    onSuccess: async (view: NotificationPreferences) => {
      queryClient.setQueryData(notificationKeys.preferences, view)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: notificationKeys.workspaces }),
        queryClient.invalidateQueries({ queryKey: notificationKeys.websites }),
        queryClient.invalidateQueries({ queryKey: notificationKeys.feed }),
      ])
    },
  })
}

/**
 * The caller's notifications in one workspace; idle without an id. Refused (`403`, `404`) where the
 * caller cannot read the workspace — a 4xx is an answer, and is not retried.
 */
export function useWorkspaceNotificationPreferences(workspaceId: MaybeRefOrGetter<string | null | undefined>) {
  const id = computed(() => toValue(workspaceId) ?? '')
  return useQuery({
    queryKey: computed(() => notificationKeys.workspace(id.value)),
    queryFn: ({ signal }) => readWorkspaceNotificationPreferences(id.value, signal),
    enabled: computed(() => id.value !== ''),
  })
}

/** Written into the workspace's key, and the organization-wide view read again: it lists the choice. */
function settleWorkspace(queryClient: QueryClient, view: WorkspaceNotificationPreferences) {
  queryClient.setQueryData(notificationKeys.workspace(view.workspace_id), view)
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: notificationKeys.preferences, exact: true }),
    queryClient.invalidateQueries({ queryKey: notificationKeys.feed }),
  ])
}

/** Set or clear the caller's choices for one workspace — and so stop following it, by a clear. */
export function useUpdateWorkspaceNotificationPreferences() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ workspaceId, changes }: { workspaceId: string; changes: NotificationChoiceChange[] }) =>
      updateWorkspaceNotificationPreferences(workspaceId, { changes }),
    onSuccess: (view: WorkspaceNotificationPreferences) => settleWorkspace(queryClient, view),
  })
}

/**
 * **Follow a kind in one place, or stop**, leaving as few choices behind as the result allows: a
 * choice made for that place is cleared first, and written only if the organization-wide value it
 * falls back to is not the one wanted (`followChanges`, then `remainingFollowChanges` on the
 * answer). `write` is that place's update; the answer is its view after the last write.
 */
async function writeFollow<View extends { kinds: ScopedKindPreference[] }>(
  view: View,
  kind: NotificationKind,
  follow: boolean,
  write: (changes: NotificationChoiceChange[]) => Promise<View>,
): Promise<View> {
  const before = view.kinds.find((entry) => entry.kind === kind)
  if (!before) return view
  const first = followChanges(before, follow)
  if (first.length === 0) return view
  const answer = await write(first)
  const after = answer.kinds.find((entry) => entry.kind === kind)
  const rest = after ? remainingFollowChanges(after, follow) : []
  if (rest.length === 0) return answer
  return write(rest)
}

/**
 * **Follow a kind in a workspace, or stop** (`writeFollow`): a choice made for this workspace is
 * cleared first, and written only if the organization-wide value is not the one wanted. The answer
 * is the workspace's view after the last write.
 */
export function useFollowWorkspace() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      view,
      kind,
      follow,
    }: {
      view: WorkspaceNotificationPreferences
      kind: NotificationKind
      follow: boolean
    }): Promise<WorkspaceNotificationPreferences> =>
      writeFollow(view, kind, follow, (changes) =>
        updateWorkspaceNotificationPreferences(view.workspace_id, { changes }),
      ),
    onSuccess: (view: WorkspaceNotificationPreferences) => settleWorkspace(queryClient, view),
    // A first write that landed before the second failed has still changed what both views say.
    onError: () => queryClient.invalidateQueries({ queryKey: notificationKeys.preferences }),
  })
}

/**
 * The caller's notifications on one channel; idle without an id. Refused (`404`) for a channel of
 * no concern to this organization — a 4xx is an answer, and is not retried. A member who may be told
 * of nothing about this channel gets an empty list.
 */
export function useWebsiteNotificationPreferences(websiteId: MaybeRefOrGetter<string | null | undefined>) {
  const id = computed(() => toValue(websiteId) ?? '')
  return useQuery({
    queryKey: computed(() => notificationKeys.website(id.value)),
    queryFn: ({ signal }) => readWebsiteNotificationPreferences(id.value, signal),
    enabled: computed(() => id.value !== ''),
  })
}

/** Written into the channel's key, and the organization-wide view read again: it lists the choice. */
function settleWebsite(queryClient: QueryClient, view: WebsiteNotificationPreferences) {
  queryClient.setQueryData(notificationKeys.website(view.website_id), view)
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: notificationKeys.preferences, exact: true }),
    queryClient.invalidateQueries({ queryKey: notificationKeys.feed }),
  ])
}

/** Set or clear the caller's choices for one channel — a clear falls back to the organization-wide value. */
export function useUpdateWebsiteNotificationPreferences() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ websiteId, changes }: { websiteId: string; changes: NotificationChoiceChange[] }) =>
      updateWebsiteNotificationPreferences(websiteId, { changes }),
    onSuccess: (view: WebsiteNotificationPreferences) => settleWebsite(queryClient, view),
  })
}

/**
 * **Be told of a kind on one channel, or stop** (`writeFollow`) — « Me prévenir de chaque vente »: a
 * choice made for this channel is cleared first, and written only if the organization-wide value is
 * not the one wanted. The answer is the channel's view after the last write.
 */
export function useFollowWebsite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      view,
      kind,
      follow,
    }: {
      view: WebsiteNotificationPreferences
      kind: NotificationKind
      follow: boolean
    }): Promise<WebsiteNotificationPreferences> =>
      writeFollow(view, kind, follow, (changes) => updateWebsiteNotificationPreferences(view.website_id, { changes })),
    onSuccess: (view: WebsiteNotificationPreferences) => settleWebsite(queryClient, view),
    // A first write that landed before the second failed has still changed what both views say.
    onError: () => queryClient.invalidateQueries({ queryKey: notificationKeys.preferences }),
  })
}

/** The organization's defaults; refused without `tenancy.settings.manage`, so gate `enabled` on it. */
export function useNotificationDefaults(options: Enabled = {}) {
  return useQuery({
    queryKey: notificationKeys.defaults,
    queryFn: ({ signal }) => readNotificationDefaults(signal),
    enabled: computed(() => toValue(options.enabled) !== false),
  })
}

/**
 * Set or clear the organization's defaults. The answer is the defaults as they now stand; the
 * caller's own values may follow them, so every preference view is read again.
 */
export function useUpdateNotificationDefaults() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (changes: NotificationChoiceChange[]) => updateNotificationDefaults({ changes }),
    onSuccess: async (view: NotificationDefaults) => {
      queryClient.setQueryData(notificationKeys.defaults, view)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: notificationKeys.preferences }),
        queryClient.invalidateQueries({ queryKey: notificationKeys.feed }),
      ])
    },
  })
}

// ------------------------------------------------------------------------------- the feed ----

/**
 * **How many lines of the caller's feed are unread** — the bell's badge. Asked again every
 * `UNREAD_POLL_MS` while the page is visible, and on focus: the feed is read, never pushed. Idle
 * while `enabled` is false — outside an organization, where there is no feed to count.
 */
export function useUnreadNotificationCount(options: Enabled = {}) {
  return useQuery({
    queryKey: notificationKeys.unreadCount,
    queryFn: ({ signal }) => countNotifications({ unread: true }, signal),
    enabled: computed(() => toValue(options.enabled) !== false),
    refetchInterval: UNREAD_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    select: (answer) => answer.count,
  })
}

/**
 * **The newest lines of the caller's feed**, read and unread — the bell's list, `FEED_PAGE` of them.
 * Read when the bell opens, which is when `enabled` turns true, and again each time it does.
 */
export function useNotificationFeed(options: Enabled = {}) {
  return useQuery({
    queryKey: notificationKeys.feedPage,
    queryFn: ({ signal }) => listNotifications({ limit: FEED_PAGE }, signal),
    enabled: computed(() => toValue(options.enabled) !== false),
    refetchOnMount: 'always',
  })
}

/** Mark one line read; the list and the count are read again. */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (itemId: string) => markNotificationRead(itemId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.feed }),
  })
}

/** Mark every unread line of the caller's feed read; the list and the count are read again. */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.feed }),
  })
}
